import React from 'react';
import { HistoryIcon } from 'lucide-react';
import { cn } from '@cotera/client/v0/lib/utils';
import { useArtifactPanel } from './artifact-panel-context';
import { useArtifactTitle } from './use-artifact-title';
import { getArtifactEditLabel } from './artifact-edit-label';

type ArtifactEditIndicatorProps = {
  artifactId: string;
  artifactType: string;
  payload?: unknown;
  className?: string;
};

/**
 * Compact row for superseded artifact tool outputs — shows the edit happened
 * without repeating the full inline preview.
 */
export const ArtifactEditIndicator: React.FC<ArtifactEditIndicatorProps> = ({
  artifactId,
  artifactType,
  payload,
  className,
}) => {
  const artifactPanel = useArtifactPanel();
  const title = useArtifactTitle({
    artifactId,
    type: artifactType,
    payload,
  });
  const label = getArtifactEditLabel(artifactType, payload, title);
  const isClickable = artifactPanel !== null;

  return (
    <button
      type="button"
      onClick={
        isClickable ? () => artifactPanel.focusArtifact(artifactId) : undefined
      }
      disabled={!isClickable}
      className={cn(
        'mb-2 inline-flex max-w-full items-center gap-2 rounded-md border border-dashed border-input bg-muted/20 px-2.5 py-1.5 text-left text-xs text-muted-foreground',
        isClickable &&
          'cursor-pointer hover:border-border hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        !isClickable && 'cursor-default',
        className
      )}
      aria-label={isClickable ? `Open latest ${title}` : label}
    >
      <HistoryIcon className="size-3.5 shrink-0 opacity-70" />
      <span className="truncate">{label}</span>
    </button>
  );
};
